/**
 * Reservations-domain color pairs (DNKADM-0269) that don't map onto an existing
 * `AdminTokens`/`STATUS` value: the availability slot-picker's selected/unselected
 * toggle (`AvailabilitySlotPicker`) and the cancellation-preview refund-delta chip's
 * positive-refund pair (`ReservationActionDialogs`). LIGHT values are byte-identical
 * to the pre-0269 literals (the 0266 identity constraint); DARK follows the
 * established translucent-tint + lifted-ink ramp (mirrors `STATUS_DARK` in
 * `../tokens.ts`). The negative-refund pair reuses `STATUS.cancelled` directly via
 * `resolveMaps` — no entry needed here.
 *
 * Consumer pattern: `const rm = resolveReservationsMaps(useTheme())`.
 */
import type { Theme } from '@mui/material/styles';
import { buildTokens } from '../theme';

// The app surface ladders the bound `slotToggle.off` values below read from
// (DNKADM-0298, the same rule as calendar.ts's DNKADM-0297 U3 alignment): the
// 0269 dark ramp COPIED these values from the dark AdminTokens, so when the
// ladder moved they silently desynced — binding makes the copy structural.
const LT = buildTokens('light');
const DT = buildTokens('dark');

/** One toggle state's border/background/ink. */
interface SlotTogglePair {
  border: string;
  bg: string;
  fg: string;
}

export interface ReservationsMaps {
  /** `AvailabilitySlotPicker`'s selected ("on") / unselected ("off") slot button. */
  slotToggle: { on: SlotTogglePair; off: SlotTogglePair };
  /** Refund-delta chip's positive-refund pair (negative reuses `STATUS.cancelled`). */
  refundPositive: { bg: string; fg: string };
  /** Danger-avatar tint behind the Cancel/No-show `DialogTitle` icon (ink is `t.error`). */
  dangerAvatarBg: string;
}

const LIGHT: ReservationsMaps = {
  slotToggle: {
    on: { border: '#15633f', bg: '#e9f1ec', fg: '#15633f' },
    // off.bg was '#fff' — an exact copy of the old light token surface, bound
    // so the unselected slot keeps matching dialog paper; border/fg are the
    // picker's own light literals (never token values) and stay independent.
    off: { border: '#d6dae0', bg: LT.surface, fg: '#2a2f37' },
  },
  refundPositive: { bg: '#e9f1ec', fg: '#15633f' },
  dangerAvatarBg: '#FBEDE9',
};

const DARK: ReservationsMaps = {
  slotToggle: {
    on: { border: '#7FD3A3', bg: 'rgba(46,157,94,0.22)', fg: '#7FD3A3' },
    // 0269 ramp copies of the old dark AdminTokens (lineStrong / surface /
    // text2) — bound so the next palette change can't desync them.
    off: { border: DT.lineStrong, bg: DT.surface, fg: DT.text2 },
  },
  refundPositive: { bg: 'rgba(46,157,94,0.22)', fg: '#7FD3A3' },
  dangerAvatarBg: 'rgba(192,73,46,0.2)',
};

/** Per-mode reservations-domain pairs. Light === the pre-0269 literals. */
function buildReservationsMaps(mode: 'light' | 'dark'): ReservationsMaps {
  return mode === 'dark' ? DARK : LIGHT;
}

/** Resolve the reservations-domain pairs off the mounted theme. */
export function resolveReservationsMaps(theme: Theme): ReservationsMaps {
  return buildReservationsMaps(theme.palette.mode === 'dark' ? 'dark' : 'light');
}
